import { useEffect, useRef, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";

const SCAN_DURATION_MS = 30000; // 30s — match Anura SDK timing

const RISK_COLOR = {
  bajo: { bg: "#DCFCE7", fg: "#15803D" },
  medio: { bg: "#FEF3C7", fg: "#B45309" },
  alto: { bg: "#FEE2E2", fg: "#B91C1C" },
};

const RISK_LABEL = {
  hypertension: "Hipertensión",
  diabetes: "Diabetes",
  heart_attack: "Infarto",
  stroke: "Derrame cerebral",
  mental_stress: "Estrés mental",
};

export default function AnuraScanModal({ open, beneficiarios, onClose, onCompleted }) {
  const [step, setStep] = useState("select"); // select | scanning | results | error
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scanResult, setScanResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [error, setError] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("select");
      setProgress(0);
      setScanResult(null);
      setError("");
      setCameraReady(false);
      stopCamera();
    }
    // eslint-disable-next-line
  }, [open]);

  const stopCamera = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const requestCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
      return true;
    } catch (e) {
      setError("No pudimos acceder a tu cámara. Verifica permisos del navegador.");
      setStep("error");
      return false;
    }
  };

  const startScan = async () => {
    setStep("scanning");
    setProgress(0);
    const ok = await requestCamera();
    if (!ok) return;

    // Simulamos el progreso 0->100 en 30s
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / SCAN_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(intervalRef.current);
        finishScan();
      }
    }, 100);
  };

  const finishScan = async () => {
    try {
      const { data } = await apiClient.post("/anura/scan", { beneficiario_index: selectedIdx });
      setScanResult(data.scan);
      setQuota(data.quota);
      setStep("results");
      stopCamera();
      onCompleted && onCompleted(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
      setStep("error");
      stopCamera();
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose && onClose();
  };

  if (!open) return null;

  return (
    <div className="anura-overlay" data-testid="anura-modal" onClick={(e) => e.target.classList.contains("anura-overlay") && handleClose()}>
      <div className="anura-modal">
        <button className="anura-close" onClick={handleClose} data-testid="anura-close">
          <i className="fas fa-times" />
        </button>

        {/* ============ STEP: SELECT ============ */}
        {step === "select" && (
          <>
            <h2 className="anura-title">
              <i className="fas fa-heart-pulse" style={{ color: "#DC2626", marginRight: 10 }} />
              Chequeo de salud Anura
            </h2>
            <p className="anura-sub">
              Mediante un video selfie de 30 segundos, mediremos sin contacto los signos vitales del beneficiario.
              <br />
              <small style={{ color: "#9ca3af" }}>
                ⚠️ Versión de prueba con datos simulados. Pronto integrado con tecnología NuraLogix DeepAffex (FDA Cleared).
              </small>
            </p>

            <h3 style={{ marginTop: 18, fontSize: 14, color: "#163680" }}>¿Para quién es el chequeo?</h3>
            <div className="anura-benef-list">
              {beneficiarios.map((b, i) => (
                <label
                  key={i}
                  className={`anura-benef-row ${selectedIdx === i ? "selected" : ""}`}
                  data-testid={`benef-radio-${i}`}
                >
                  <input
                    type="radio"
                    checked={selectedIdx === i}
                    onChange={() => setSelectedIdx(i)}
                  />
                  <div>
                    <strong>{b.nombre}</strong>
                    <small>{b.relacion} · {b.municipio}, {b.estado}</small>
                  </div>
                </label>
              ))}
            </div>

            <div className="anura-actions">
              <button className="btn btn-secondary" onClick={handleClose} data-testid="anura-cancel">Cancelar</button>
              <button className="btn btn-primary" onClick={startScan} data-testid="anura-start-btn">
                <i className="fas fa-camera" /> Iniciar escaneo
              </button>
            </div>
          </>
        )}

        {/* ============ STEP: SCANNING ============ */}
        {step === "scanning" && (
          <>
            <h2 className="anura-title">Capturando signos vitales…</h2>
            <p className="anura-sub">Mantén el rostro centrado en el recuadro y respira con normalidad.</p>

            <div className="anura-cam-wrap">
              <video ref={videoRef} muted playsInline className="anura-video" />
              <div className="anura-face-frame" />
              {!cameraReady && <div className="anura-cam-loading">Iniciando cámara…</div>}
            </div>

            <div className="anura-progress" data-testid="anura-progress">
              <div className="anura-progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <p style={{ textAlign: "center", color: "#6b7280", marginTop: 8 }}>
              {Math.round(progress)}% · {Math.max(0, Math.ceil((SCAN_DURATION_MS - (progress / 100) * SCAN_DURATION_MS) / 1000))}s restantes
            </p>
          </>
        )}

        {/* ============ STEP: RESULTS ============ */}
        {step === "results" && scanResult && (
          <ResultsView
            scan={scanResult}
            quota={quota}
            beneficiario={beneficiarios[selectedIdx]}
            onClose={handleClose}
          />
        )}

        {/* ============ STEP: ERROR ============ */}
        {step === "error" && (
          <>
            <h2 className="anura-title" style={{ color: "#B91C1C" }}>
              <i className="fas fa-exclamation-triangle" /> No se pudo completar
            </h2>
            <p className="anura-sub" data-testid="anura-error">{error}</p>
            <div className="anura-actions">
              <button className="btn btn-secondary" onClick={handleClose}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { setStep("select"); setError(""); }}>
                Reintentar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResultsView({ scan, quota, beneficiario, onClose }) {
  const v = scan.results.vitals;
  const r = scan.results.risks;
  const alerts = scan.results.alerts || [];

  return (
    <>
      <h2 className="anura-title">
        <i className="fas fa-check-circle" style={{ color: "#15803D" }} />
        Resultados del chequeo
      </h2>
      <p className="anura-sub">
        <strong>{beneficiario.nombre}</strong> · {scan.results.demographics.age} años
        <small style={{ color: "#9ca3af", marginLeft: 10 }}>
          {new Date(scan.created_at).toLocaleString("es-MX")}
        </small>
      </p>

      {alerts.length > 0 && (
        <div style={{ background: "#FEF2F2", border: "1px solid #DC2626", borderRadius: 10, padding: 12, margin: "14px 0" }} data-testid="anura-alerts">
          {alerts.map((a, i) => (
            <div key={i} style={{ fontSize: 13, color: "#7F1D1D", margin: "4px 0" }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />
              {a.message}
            </div>
          ))}
        </div>
      )}

      <h3 className="anura-section-title">Signos vitales</h3>
      <div className="anura-vitals-grid">
        <VitalBox label="Ritmo cardíaco" value={v.heart_rate_bpm} unit="bpm" icon="fa-heart-pulse" color="#DC2626" />
        <VitalBox label="Respiración" value={v.breathing_rate_rpm} unit="rpm" icon="fa-lungs" color="#0369A1" />
        <VitalBox label="Presión arterial" value={`${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}`} unit="mmHg" icon="fa-droplet" color="#B91C1C" />
        <VitalBox label="Oxígeno (SpO₂)" value={v.spo2_percent} unit="%" icon="fa-wind" color="#0891B2" />
        <VitalBox label="Variabilidad cardíaca" value={v.hrv_ms} unit="ms" icon="fa-wave-square" color="#7C3AED" />
        <VitalBox label="Nivel de estrés" value={v.stress_index} unit="/100" icon="fa-brain" color="#EA580C" />
      </div>

      <div className="anura-wellness">
        <div>
          <span style={{ color: "#6b7280", fontSize: 13 }}>Índice de bienestar</span>
          <strong style={{ fontSize: 36, color: v.wellness_score >= 75 ? "#15803D" : v.wellness_score >= 50 ? "#B45309" : "#B91C1C" }}>
            {v.wellness_score}<small style={{ fontSize: 18, color: "#9ca3af" }}>/100</small>
          </strong>
        </div>
        <div className="anura-wellness-bar">
          <div
            className="anura-wellness-fill"
            style={{
              width: `${v.wellness_score}%`,
              background: v.wellness_score >= 75 ? "#7DC242" : v.wellness_score >= 50 ? "#F59E0B" : "#DC2626",
            }}
          />
        </div>
      </div>

      <h3 className="anura-section-title">Indicadores de riesgo</h3>
      <div className="anura-risks-grid" data-testid="anura-risks">
        {Object.entries(r).map(([key, lvl]) => (
          <div key={key} className="anura-risk-pill" style={{ background: RISK_COLOR[lvl].bg, color: RISK_COLOR[lvl].fg }}>
            <strong>{RISK_LABEL[key] || key}</strong>
            <span>{lvl.toUpperCase()}</span>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 16, textAlign: "center" }}>
        ⚕️ Esta medición no reemplaza una consulta médica. Comparte los resultados con un profesional si detecta indicadores en rojo.
      </p>

      {quota && (
        <p style={{ fontSize: 12, color: "#6b7280", textAlign: "center", margin: "8px 0 14px" }}>
          Te quedan <strong>{quota.scans_remaining}</strong> de {quota.quota_total} escaneos este año.
        </p>
      )}

      <div className="anura-actions">
        <button className="btn btn-primary" onClick={onClose} data-testid="anura-finish">
          Cerrar
        </button>
      </div>
    </>
  );
}

function VitalBox({ label, value, unit, icon, color }) {
  return (
    <div className="anura-vital-box">
      <i className={`fas ${icon}`} style={{ color, fontSize: 18 }} />
      <span className="anura-vital-label">{label}</span>
      <strong className="anura-vital-value">
        {value}<small>{unit}</small>
      </strong>
    </div>
  );
}
